import { Typography } from '@mui/material';
import { Grid, Button, FormControlLabel, Checkbox, Slider, Box, Divider, Skeleton, Switch } from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from './../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { resolvePartnerModifySettingsAllowed } from 'utils/partnerUiCapabilities';
import { useEffect, useState } from 'react';

import { setDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  microphoneVolume: {
    volume_level: 0,
    submit: null
  }
};

const MicrophoneVolumeSettingsPage = () => {
  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [microphoneVolumeSettings, setMicrophoneVolumeSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    // console.log('SelectedDevice ---->', selectedDevice);
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        // console.log('statusRef data is - - -', status.data());
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 5 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        // console.log('device online status is ->', deviceStatus);

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const marks = [
    {
      value: 0,
      label: '0'
    },
    {
      value: 1,
      label: '1'
    },
    {
      value: 2,
      label: '2'
    },
    {
      value: 3,
      label: '3'
    },
    {
      value: 4,
      label: '4'
    },
    {
      value: 5,
      label: '5'
    },
    {
      value: 6,
      label: '6'
    },
    {
      value: 7,
      label: '7'
    },
    {
      value: 8,
      label: '8'
    },
    {
      value: 9,
      label: '9'
    },
    {
      value: 10,
      label: '10'
    },
    {
      value: 11,
      label: '11'
    },
    {
      value: 12,
      label: '12'
    },
    {
      value: 13,
      label: '13'
    },
    {
      value: 14,
      label: '14'
    },
    {
      value: 15,
      label: '15'
    }
  ];

  useEffect(() => {
    // console.log(selectedDevice)
    if (!selectedDevice) return;
    setLoading(true);
    (async () => {
      await FirestoreDataService.getDeviceSettings(selectedDevice)
        .then((documentSnap) => {
          if (documentSnap.exists()) {
            let microphoneVolume = documentSnap.data().microphone_volume;
            // console.log('Fetched:-', microphoneVolume);
            let microphoneVolumeObject = { volume_level: microphoneVolume };
            setMicrophoneVolumeSettings(microphoneVolumeObject);
          }
        })
        .catch((e) => {
          const msg = 'Failed to get microphone volume settings!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
          console.info(msg, e);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [selectedDevice]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary">
          Microphone Volume Settings
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" height={230} />
          </>
        ) : (
          <Formik
            initialValues={microphoneVolumeSettings ? microphoneVolumeSettings : INITIAL_VALUES}
            validationSchema={Yup.object().shape({
              microphoneVolume: Yup.object()
                .shape({
                  volume_level: Yup.number()
                })
                .required()
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                setStatus({ success: false });
                setSubmitting(true);
                // console.log(values);

                // Check permission: Redux appUser, partner sessionStorage, Smart Monitor ui_capabilities (canEditDeviceSettings)
                const canModifySettings = resolvePartnerModifySettingsAllowed(appUser);
                if (!canModifySettings) {
                  dispatch(
                    showAppAlert({
                      show: true,
                      alertType: 'error',
                      alertMessage: 'Error: You do not have permission to save these settings.'
                    })
                  );
                  return;
                }

                let payload = { volume_level: parseInt(values.volume_level) };
                // console.log('payload', payload);

                const tasksRef = doc(db, 'tasks', selectedDevice);
                setDoc(tasksRef, {
                  device_id: selectedDevice,
                  type: 'microphone_volume',
                  user_id: appUser.email ? appUser.email : '-',
                  old_value: microphoneVolumeSettings,
                  payload
                })
                  .then((response) => {
                    // console.log('saved tasks!', response);
                    const msg = 'Microphone volume settings saved successfully !';
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    const msg = 'Failed to save microphone volume Settings!';
                    console.log(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  });
              } catch (err) {
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save microphone volume settings!' }));
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    {/* <Typography variant="caption" color="textSecondary">
                      {JSON.stringify(values)}
                    </Typography> */}
                    <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex' }}>
                      <Typography variant="h6" color="textSecondary">
                        {'Volume Level (0-15)'}
                      </Typography>
                    </Grid>
                    <Slider
                      aria-label="volume_level"
                      name="volume_level"
                      id="volume_level"
                      marks={marks}
                      onChange={handleChange}
                      value={values.volume_level ? values.volume_level : 0}
                      valueLabelDisplay="auto"
                      step={1}
                      min={0}
                      max={15}
                    />

                    <Grid item xs={12} sm={12} md={12} lg={12} style={{ paddingTop: 16, paddingBottom: 16, marginBottom: 16 }}>
                      <Button disableElevation style={{ marginLeft: 16, float: 'right' }} type="submit" variant="contained" color="primary">
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        )}
      </MainCard>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </Grid>
  );
};

export default MicrophoneVolumeSettingsPage;
